import type { PoolShippingDetail } from '../../lib/api/operatorConsole';
import { formatAmount } from './formatMoney';
import { StatusPill } from './StatusPill';

interface Props {
  shipping: PoolShippingDetail;
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-[11px] font-semibold uppercase tracking-wide text-ui-text-secondary">
        {label}
      </dt>
      <dd className="mt-0.5 text-sm text-ui-text">{children}</dd>
    </div>
  );
}

function addressLines(shipping: PoolShippingDetail): string[] {
  const address = shipping.address;
  if (!address) return [];

  return [
    address.name,
    [address.first_line, address.second_line, address.apartment_number]
      .filter(Boolean)
      .join(', '),
    [address.city, address.state, address.zipcode].filter(Boolean).join(', '),
    address.country,
    address.phone_number,
  ].filter((line): line is string => Boolean(line));
}

export function ShippingFacts({ shipping }: Props) {
  const lines = addressLines(shipping);
  // Tracking only exists once the parcel is out; saying "—" beforehand is
  // honest, an empty gap would read as missing data.
  const tracking = shipping.tracking_number
    ? `${shipping.tracking_number}${shipping.tracking_carrier ? ` · ${shipping.tracking_carrier}` : ''}`
    : 'Not shipped yet';

  return (
    <section
      aria-label="Delivery summary"
      className="rounded-lg border border-ui-border bg-ui-card p-4"
    >
      <dl className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <Fact label="Created">{new Date(shipping.created_at).toLocaleString()}</Fact>
        <Fact label="Status">
          <StatusPill shipping={shipping} />
          {shipping.settle_summary && (
            <div className="mt-1 text-[11px] text-ui-text-secondary capitalize">
              {shipping.settle_summary.replace(/_/g, ' ')}
            </div>
          )}
        </Fact>
        <Fact label="Player">
          <span className="font-mono">{shipping.operator_account_id ?? '—'}</span>
          <div className="text-[11px] text-ui-text-secondary">user #{shipping.user_id}</div>
        </Fact>
        <Fact label="Shipping type">
          <span className="capitalize">{shipping.shipping_type.replace(/_/g, ' ')}</span>
          {shipping.ship_my_cards_nickname && (
            <div className="text-[11px] text-ui-text-secondary">
              {shipping.ship_my_cards_nickname}
            </div>
          )}
        </Fact>
        <Fact label="Total paid">
          <span className="font-mono">
            {formatAmount(shipping.total_amount, shipping.currency)}
          </span>
        </Fact>
        <Fact label="Tracking">{tracking}</Fact>
        <Fact label="Cards">{shipping.cards_count}</Fact>
        <Fact label="Refunded cards">{shipping.refunded_count}</Fact>
      </dl>

      <div className="mt-4 border-t border-ui-border pt-3">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-ui-text-secondary">
          Address
        </div>
        {lines.length === 0 ? (
          <p className="mt-1 text-sm text-ui-text-secondary">No address on this delivery.</p>
        ) : (
          <address className="mt-1 text-sm not-italic text-ui-text">
            {lines.map((line) => (
              <div key={line}>{line}</div>
            ))}
          </address>
        )}
      </div>
    </section>
  );
}
